import React, { useCallback } from "react";
import * as Mui from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { BranchingSection, Field } from "@/types/form";
import { fieldTypes } from "@/utils/constants";
import { FormAction } from "@/scripts/formAuthoringReducer";
import FieldOptions from "./FieldOptions";
import AddIcon from "@mui/icons-material/Add";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";

interface FieldEditorProps {
  sectionId: string;
  field: Field;
  editEnabled: boolean;
  isTitleField: boolean | undefined;
  isBranchingField: boolean | undefined;
  branchingOptions: string[] | undefined;
  branchingSections: BranchingSection[] | undefined;
  isLastField: boolean;
  isFirstField: boolean;
  onSetEditFieldId: (fieldId: string) => void;
  dispatch: React.Dispatch<FormAction>;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  sectionId,
  field,
  editEnabled,
  isBranchingField,
  branchingOptions,
  branchingSections,
  isTitleField,
  isLastField,
  isFirstField,
  onSetEditFieldId,
  dispatch,
}) => {
  const handleSetEditFieldId = useCallback(
    () => onSetEditFieldId(field.id),
    [field.id, onSetEditFieldId]
  );

  const handleUpdateFieldLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      dispatch({
        type: "UPDATE_FIELD_LABEL",
        payload: { sectionId, fieldId: field.id, label: e.target.value },
      }),
    [dispatch, field.id, sectionId]
  );

  const handleUpdateFieldType = useCallback(
    (e: Mui.SelectChangeEvent<Field["type"]>) =>
      dispatch({
        type: "UPDATE_FIELD_TYPE",
        payload: {
          sectionId,
          fieldId: field.id,
          fieldType: e.target.value as string,
        },
      }),
    [dispatch, field.id, sectionId]
  );

  const handleToggleRequired = useCallback(
    () =>
      dispatch({
        type: "TOGGLE_REQUIRED",
        payload: { sectionId, fieldId: field.id },
      }),
    [dispatch, field.id, sectionId]
  );

  const handleToggleWithTime = useCallback(
    () =>
      dispatch({
        type: "TOGGLE_WITH_TIME",
        payload: { sectionId, fieldId: field.id },
      }),
    [dispatch, field.id, sectionId]
  );

  const handleToggleIsTitleField = useCallback(
    () =>
      dispatch({
        type: "SET_TITLE_FIELD",
        payload: { fieldId: field.id },
      }),
    [dispatch, field.id]
  );

  const handleToggleBranchingField = useCallback(
    () =>
      dispatch({
        type: "TOGGLE_BRANCHING",
        payload: { sectionId, fieldId: field.id },
      }),
    [dispatch, field.id, sectionId]
  );

  const handleDeleteField = useCallback(
    () =>
      dispatch({
        type: "DELETE_FIELD",
        payload: { sectionId, fieldId: field.id },
      }),
    [dispatch, field.id, sectionId]
  );

  const moveFieldUp = useCallback(() => {
    dispatch({
      type: "MOVE_FIELD",
      payload: { sectionId, fieldId: field.id, up: true },
    });
  }, [dispatch, field.id, sectionId]);

  const moveFieldDown = useCallback(() => {
    dispatch({
      type: "MOVE_FIELD",
      payload: { sectionId, fieldId: field.id, up: false },
    });
  }, [dispatch, field.id, sectionId]);

  const handleAddOption = useCallback(() => {
    dispatch({
      type: "ADD_FIELD_OPTION",
      payload: { sectionId, fieldId: field.id },
    });
  }, [dispatch, field.id, sectionId]);

  return (
    <>
      <Mui.Paper className="p-4 mb-4 relative" onClick={handleSetEditFieldId}>
        {!editEnabled && <div className="h-full w-full z-10 absolute -m-4" />}
        <Mui.Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          margin={2}
        >
          <Mui.TextField
            label="Field Label"
            value={field.label}
            onChange={handleUpdateFieldLabel}
            disabled={!editEnabled}
            fullWidth
            style={{ flex: 1, marginRight: "10px" }}
          />
          {editEnabled && (
            <Mui.FormControl style={{ flex: 1 }}>
              <Mui.InputLabel>Field Type</Mui.InputLabel>
              <Mui.Select
                value={field.type}
                onChange={handleUpdateFieldType}
                fullWidth
              >
                {fieldTypes.map((type) => (
                  <Mui.MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </Mui.MenuItem>
                ))}
              </Mui.Select>
            </Mui.FormControl>
          )}
        </Mui.Box>

        {/* Field Preview */}
        {field.type === "text" && (
          <Mui.TextField disabled placeholder="Text Input" fullWidth />
        )}
        {field.type === "textarea" && (
          <Mui.TextField
            disabled
            placeholder="Textarea"
            multiline
            rows={4}
            fullWidth
          />
        )}
        {(field.type === "radio" || field.type === "checkbox") && (
          <>
            {field.options?.map((option: string, optionIndex: number) => (
              <FieldOptions
                key={optionIndex}
                dispatch={dispatch}
                sectionId={sectionId}
                optionIndex={optionIndex}
                option={option}
                editEnabled={editEnabled}
                isBranchingField={isBranchingField!}
                branchingSections={branchingSections!}
                branchingOptions={branchingOptions}
                field={field}
              />
            ))}
            {editEnabled && (
              <Mui.Button
                variant="outlined"
                size="small"
                onClick={handleAddOption}
                startIcon={<AddIcon />}
                sx={{ mt: 1, ml: 1 }}
              >
                Add Option
              </Mui.Button>
            )}
          </>
        )}
        {field.type === "date" && (
          <Mui.TextField disabled placeholder="Date Picker" fullWidth />
        )}
        {field.type === "upload" && (
          <Mui.Box display="flex" alignItems="center">
            <UploadFileIcon />
            <Mui.Typography variant="body1">Upload File</Mui.Typography>
          </Mui.Box>
        )}

        {/* Required Toggle and Delete Button */}
        {editEnabled && (
          <Mui.Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Mui.Box className="mt-4">
              {/* Move Field Up */}
              <Mui.IconButton
                onClick={moveFieldUp}
                color="primary"
                disabled={isFirstField}
              >
                <ArrowUpward />
              </Mui.IconButton>
              {/* Move Field Down */}
              <Mui.IconButton
                onClick={moveFieldDown}
                color="primary"
                disabled={isLastField}
              >
                <ArrowDownward />
              </Mui.IconButton>
            </Mui.Box>
            <Mui.Box display="flex" alignItems="center">
              {/* Process Title */}
              {field.type === "text" && (
                <>
                  <Mui.Typography>Title</Mui.Typography>
                  <Mui.Switch
                    checked={isTitleField!}
                    disabled={isTitleField!}
                    onChange={handleToggleIsTitleField}
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                </>
              )}
              {field.type === "radio" && (
                <>
                  <Mui.Typography>Branching</Mui.Typography>
                  <Mui.Switch
                    checked={isBranchingField}
                    onChange={handleToggleBranchingField}
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                </>
              )}
              {field.type === "date" && (
                <>
                  <Mui.Typography>Include time</Mui.Typography>
                  <Mui.Switch
                    checked={field.withTime ?? false}
                    onChange={handleToggleWithTime}
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                </>
              )}
              <Mui.Typography>Required</Mui.Typography>
              <Mui.Switch
                checked={field.required}
                onChange={handleToggleRequired}
                color="primary"
              />
              <Mui.IconButton onClick={handleDeleteField} color="primary">
                <DeleteIcon />
              </Mui.IconButton>
            </Mui.Box>
          </Mui.Box>
        )}
      </Mui.Paper>
    </>
  );
};

export default React.memo(FieldEditor);
